import React, { ReactNode } from 'react';
import { useTable, Column, useExpanded, useFilters } from 'react-table';
import { RowTemplate } from './BasicRowTemplate';
import { FilterAltOutlined } from '@mui/icons-material';
import { Button } from '@mui/material';

export interface BasicColumn {
  name: string;
  value: string;
  formatter?: (_column: Row) => ReactNode;
  width?: number;
}

export type Row = any;

export interface ITableOptions {
  cellClass?: string;
  noDataMsg?: string;
  allowExpand?: boolean;
  expandDataKey?: string;
  hideHeader?: boolean;
  firstTdClass?: string;
  rowClick?: (_row: Row) => void;
  noBorders?: boolean;
  filters?: { [key: string]: boolean };
}
interface TableProps {
  allRows: Row[];
  columns: BasicColumn[];
  options?: ITableOptions;
}

const BasicTable: React.FC<TableProps> = ({ allRows, columns, options }) => {
  const colDefs = React.useMemo(
    (): Column<Row>[] =>
      columns.map(({ name, value, formatter, width }) => ({
        Header: name,
        accessor: value,
        Cell: (data: Row) => formatter?.(data) || <span>{data?.value?.toString() || ''}</span>,
        width,
        formatter,
      })),

    [columns],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<Row>(
    {
      columns: colDefs,
      data: allRows,
    },
    useFilters,
    useExpanded,
  );
  const [filters, setFilters] = React.useState<any>({});
  const changeFilterVal = (header: string, field: string, value: string) => {
    setFilters((fil: any) => ({
      ...fil,
      [header.toString() || '']: {
        ...fil[header.toString() || ''],
        [field]: value,
      },
    }));
  };

  const isRowVisible = (row: any, currFilters: Record<string, Record<string, string>>): boolean =>
    !Object.entries(currFilters).some(
      ([key, filter]) =>
        !(typeof row.original[key] === 'object' ? JSON.stringify(row.original[key]) : row.original[key] || '').includes(
          filter?.text || '',
        ),
    );

  React.useEffect(() => {
    console.log('Table re-rendered');
  }, [rows]);

  return (
    <div className="flex flex-1 overflow-y-auto p-2">
      <table {...getTableProps()} className="flex-1 overflow-scroll table-auto">
        {options?.hideHeader ? null : (
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} key="Nope">
                {headerGroup.headers.map((column, i) => (
                  <th
                    {...column.getHeaderProps()}
                    className="bg-gray-50 text-secondary_text font-normal text-base p-2 "
                    key={i}
                  >
                    <div>
                      {column.render('Header')}
                      {options?.filters?.[column.Header?.toString() || ''] && (
                        <Button onClick={() => changeFilterVal(column.Header?.toString() || '', 'visible', 'true')}>
                          <FilterAltOutlined />
                        </Button>
                      )}
                    </div>

                    {filters[column.Header?.toString() || '']?.visible && (
                      <input
                        value={filters[column.Header?.toString() || ''].text}
                        onChange={({ target }) =>
                          changeFilterVal(column.Header?.toString() || '', 'text', target.value)
                        }
                      />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
        )}
        <tbody {...getTableBodyProps()}>
          {rows
            .filter((r) => isRowVisible(r, filters))
            .map((row, i) => {
              prepareRow(row);
              return <RowTemplate row={row} i={i} options={options} key={'rt' + i} />;
            })}
          {!rows.filter((r) => isRowVisible(r, filters))?.length && (
            <tr>
              <td colSpan={colDefs?.length || 2}>
                <div className="flex flex-1 justify-center p-20">
                  <div>{options?.noDataMsg || 'No data'}</div>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default BasicTable;
